import { Component, OnInit } from "@angular/core";
import StatisticsData from "src/assets/statistics.json";

@Component({
  selector: "app-case-number",
  templateUrl: "./case-number.component.html",
  styleUrls: ["./case-number.component.scss"],
})
export class CaseNumberComponent implements OnInit {
  options: any;
  xAxisInterval: number;
  dateShownNumberInSmallScreen = 8;
  dateShownNumberInLargeScreen = 10;
  dateShownNumberInSuperLargeScreen = 15;
  confirmedVicDate;
  confirmedVicToday;
  confirmedVicDelta;
  confirmedAuDate;
  confirmedAuToday;
  confirmedAuDelta;
  recoveredAuDate;
  recoveredAuToday;
  recoveredAuDelta;
  deathsAuDate;
  deathsAuToday;
  deathsAuDelta;

  constructor() {}

  ngOnInit() {
    const dateLength = RAW_DATA.deaths_au_arr.length;

    if (window.innerWidth < 550) {
      this.xAxisInterval = Math.max(
        Math.floor(dateLength / this.dateShownNumberInSmallScreen) - 1,
        0
      );
    } else if (window.innerWidth < 1000) {
      this.xAxisInterval = Math.max(
        Math.floor(dateLength / this.dateShownNumberInLargeScreen) - 1,
        0
      );
    } else {
      this.xAxisInterval = Math.max(
        Math.floor(dateLength / this.dateShownNumberInSuperLargeScreen) - 1,
        0
      );
    }

    this.options = {
      toolbox: {
        show: true,
        feature: {
          mark: { show: true },
          dataView: {
            show: true,
            readOnly: true,
            title: "Data View",
            lang: ["Data View", "Close"],
          },
          saveAsImage: { show: true, title: "Save as Picture" },
        },
      },
      title: {
        text: "Case Statistics in VIC & AU",
        x: "center",
      },
      tooltip: {
        trigger: "axis",
      },
      grid: {
        top: "25%",
        containLabel: true,
      },
      legend: {
        top: "6%",
        data: ["AU Deaths", "AU Recovered", "VIC Confirmed", "AU Confirmed"],
      },
      xAxis: [
        {
          type: "category",
          axisTick: {
            alignWithLabel: true,
          },
          splitLine: {
            show: false,
          },
          splitArea: {
            show: false,
          },
          axisLabel: {
            interval: this.xAxisInterval,
            rotate: 50,
          },
          data: RAW_DATA.date_arr,
        },
      ],
      yAxis: [
        {
          type: "value",
          name: "AU Deaths & Recovered",
          min: 0,
          position: "left",
          axisTick: {
            show: false,
          },
          axisLabel: {
            interval: 0,
          },
          splitArea: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
        {
          type: "value",
          name: "VIC & AU Confirmed",
          min: 0,
          position: "right",
          axisTick: {
            show: false,
          },
          axisLabel: {
            interval: 0,
          },
          splitArea: {
            show: false,
          },
          splitLine: {
            show: false,
          },
        },
      ],
      dataZoom: [
        {
          type: "slider",
          xAxisIndex: 0,
          filterMode: "empty",
          start: this.setDataZoomStart(),
          end: 100,
        },
        {
          type: "inside",
          xAxisIndex: 0,
          filterMode: "empty",
          start: 0,
          end: 100,
        },
      ],
      series: [
        {
          name: "AU Deaths",
          type: "line",
          label: {
            normal: {
              show: true,
              position: "top",
            },
          },
          lineStyle: {
            normal: {
              width: 3,
              shadowColor: "rgba(0,0,0,0.4)",
              shadowBlur: 10,
              shadowOffsetY: 10,
            },
          },
          data: RAW_DATA.deaths_au_arr,
        },
        {
          name: "AU Recovered",
          type: "line",
          label: {
            normal: {
              show: true,
              position: "top",
            },
          },
          lineStyle: {
            normal: {
              width: 3,
              shadowColor: "rgba(0,0,0,0.4)",
              shadowBlur: 10,
              shadowOffsetY: 10,
            },
          },
          data: RAW_DATA.recovered_au_arr,
        },
        {
          name: "VIC Confirmed",
          type: "bar",
          yAxisIndex: 1,
          label: {
            normal: {
              show: true,
              position: "top",
            },
          },
          data: RAW_DATA.confirmed_vic_arr,
        },
        {
          name: "AU Confirmed",
          type: "bar",
          yAxisIndex: 1,
          label: {
            normal: {
              show: true,
              position: "top",
            },
          },
          data: RAW_DATA.confirmed_au_arr,
        },
      ],
    };

    this.processTodayNumber();
  }

  private setDataZoomStart() {
    if (window.innerWidth >= 1000) {
      return 80;
    } else if (window.innerWidth >= 700) {
      return 90;
    }
    return 95;
  }

  private processTodayNumber() {
    [
      this.confirmedVicDate,
      this.confirmedVicToday,
      this.confirmedVicDelta,
    ] = this.getLastValidData("confirmed_vic_arr");
    [
      this.confirmedAuDate,
      this.confirmedAuToday,
      this.confirmedAuDelta,
    ] = this.getLastValidData("confirmed_au_arr");
    [
      this.recoveredAuDate,
      this.recoveredAuToday,
      this.recoveredAuDelta,
    ] = this.getLastValidData("recovered_au_arr");
    [
      this.deathsAuDate,
      this.deathsAuToday,
      this.deathsAuDelta,
    ] = this.getLastValidData("deaths_au_arr");
  }

  private getLastValidData(field: string) {
    const arr = RAW_DATA[field];
    for (let i = arr.length - 1; i >= 0; i--) {
      const arr = RAW_DATA[field];
      if (arr[i]) {
        return [
          RAW_DATA["date_arr"][i],
          RAW_DATA[field][i],
          RAW_DATA[field][i] - RAW_DATA[field][i - 1],
        ];
      }
    }
  }
}

export interface StatisticsDataType {
  date_arr: string[];
  confirmed_vic_arr: string[];
  confirmed_au_arr: string[];
  recovered_au_arr: string[];
  deaths_au_arr: string[];
}

const RAW_DATA: StatisticsDataType = StatisticsData;
